import { useState, useCallback } from 'react';
import sellerDataApi from '../../../api/sellerDataApi'; // Adjust path if needed

function useSellerData(id) {
  const [sellerData, setSellerData] = useState(null);
  const [loadingAd, setLoadingAd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [error, setError] = useState(null);

  const fetchSellerData = useCallback(async () => {
    try {
      const response = await sellerDataApi.fetchSellerData(id);
      setSellerData(response.data);
    } catch (err) {
      console.error('Error fetching seller data:', err);
      setError('Failed to fetch seller data.');
    }
  }, [id]);

  const handleGenerateAd = async () => {
    setLoadingAd(true);
    setError(null);
    try {
      const response = await sellerDataApi.generateAd(id);
      setSellerData((prev) => ({
        ...prev,
        ad: response.data.ad,
      }));
    } catch (err) {
      console.error('Error generating ad:', err);
      setError('Failed to generate ad.');
    } finally {
      setLoadingAd(false);
    }
  };

  /**
   * Conditionally decide which route to call:
   * - If we are updating "ad", we do POST /api/update-ad
   * - Else, we do PUT /api/update-seller-data/<id>
   */
  const handleSellerDataUpdate = useCallback(
    async (updatedData) => {
      setLoadingUpdate(true);
      setError(null);
      try {
        
        if ('ad' in updatedData) {
          // Updating the "ad" only
          const payload = {
            seller_data_id: id,
            ad: updatedData.ad,
          };
          const response = await sellerDataApi.updateAd(payload);

          // Merge the updated ad into local state
          setSellerData((prev) => ({
            ...prev,
            ad: response.data.ad,
          }));
          
          return response.data;
        } else {
          // Updating any other fields (results, phone, email, property_type, etc.)
          const response = await sellerDataApi.updateSellerDataAll(id, updatedData);

          // Immediately fetch fresh data from server to ensure we have the latest
          await fetchSellerData();
          
          return response.data;
        }
      } catch (err) {
        console.error('Error updating seller data:', err);
        setError('Failed to update seller data: ' + (err.response?.data?.error || err.message));
        return { error: err.response?.data?.error || err.message };
      } finally {
        setLoadingUpdate(false);
      }
    },
    [id, fetchSellerData]
  );

  const handleDeleteSellerData = useCallback(async () => {
    setLoadingUpdate(true);
    setError(null);
    try {
      const response = await sellerDataApi.deleteSellerData(id);
      return response.data;
    } catch (err) {
      console.error('Error deleting seller data:', err);
      setError('Failed to delete seller data.');
      throw err;
    } finally {
      setLoadingUpdate(false);
    }
  }, [id]);

  return {
    sellerData,
    loadingAd,
    loadingUpdate,
    error,
    fetchSellerData,
    handleGenerateAd,
    handleSellerDataUpdate,
    handleDeleteSellerData,
  };
}

export default useSellerData;
